/**
 * AbsentRepeatOffendersTable — sibling of RepeatOffendersTable but for
 * the Absent & Leave Review report.
 *
 * Columns: Staff · Geofence · Department · Days Off (total)
 *          · % of Total · Absent days · Leave (Approved) · Leave (Pending)
 *          · Last Date
 *
 * Same per-column filter + sort affordances as the late-comers variant
 * (May 29, 2026 user pattern). Threshold is 2 days off (user ask
 * "Keep +2 days as alert"). Even 1-day rows still appear in the main
 * data table below — this card only highlights the repeats.
 */
import React, { useState, useMemo } from 'react';
import { Search, ArrowUp, ArrowDown, ArrowUpDown, X } from 'lucide-react';

const NUM_COLS = ['total', 'pct', 'absent', 'leave_approved', 'leave_pending'];

export default function AbsentRepeatOffendersTable({ offenders, threshold, grandTotal }) {
  const [filters, setFilters] = useState({
    name: '', geofence: '', department: '',
    total_min: '', total_max: '',
    pct_min: '', pct_max: '',
    absent_min: '', absent_max: '',
    leave_approved_min: '', leave_approved_max: '',
    leave_pending_min: '', leave_pending_max: '',
    last_date_min: '', last_date_max: '',
  });
  const [sortBy, setSortBy] = useState({ key: 'total', dir: 'desc' });

  const decorated = useMemo(
    () => offenders.map(o => ({
      ...o,
      pct: grandTotal > 0 ? (o.total / grandTotal) * 100 : 0,
    })),
    [offenders, grandTotal]
  );

  const filtered = useMemo(() => {
    const ci = (s) => (s || '').toLowerCase();
    const text = {
      name: ci(filters.name),
      geofence: ci(filters.geofence),
      department: ci(filters.department),
    };
    const inRange = (val, min, max) => {
      if (min !== '' && Number(val) < Number(min)) return false;
      if (max !== '' && Number(val) > Number(max)) return false;
      return true;
    };
    const inDateRange = (val, min, max) => {
      // ISO YYYY-MM-DD strings compare lexicographically.
      if (min && (!val || val < min)) return false;
      if (max && val && val > max) return false;
      return true;
    };
    return decorated.filter(r =>
      ci(`${r.name} ${r.sid}`).includes(text.name) &&
      ci(r.geofence).includes(text.geofence) &&
      ci(r.department).includes(text.department) &&
      inRange(r.total, filters.total_min, filters.total_max) &&
      inRange(r.pct, filters.pct_min, filters.pct_max) &&
      inRange(r.absent, filters.absent_min, filters.absent_max) &&
      inRange(r.leave_approved, filters.leave_approved_min, filters.leave_approved_max) &&
      inRange(r.leave_pending, filters.leave_pending_min, filters.leave_pending_max) &&
      inDateRange(r.last_date, filters.last_date_min, filters.last_date_max)
    );
  }, [decorated, filters]);

  const sorted = useMemo(() => {
    if (!sortBy.key) return filtered;
    const sign = sortBy.dir === 'asc' ? 1 : -1;
    const isNum = NUM_COLS.includes(sortBy.key);
    return [...filtered].sort((a, b) => {
      const av = a[sortBy.key];
      const bv = b[sortBy.key];
      if (isNum) return (Number(av || 0) - Number(bv || 0)) * sign;
      return String(av || '').localeCompare(String(bv || '')) * sign;
    });
  }, [filtered, sortBy]);

  const handleSort = (key) => {
    setSortBy(prev => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: null, dir: null };
    });
  };

  const clearFilters = () => setFilters({
    name: '', geofence: '', department: '',
    total_min: '', total_max: '',
    pct_min: '', pct_max: '',
    absent_min: '', absent_max: '',
    leave_approved_min: '', leave_approved_max: '',
    leave_pending_min: '', leave_pending_max: '',
    last_date_min: '', last_date_max: '',
  });

  const hasAnyFilter = Object.values(filters).some(v => v !== '');

  const subTotal = sorted.reduce((acc, s) => ({
    total: acc.total + (s.total || 0),
    absent: acc.absent + (s.absent || 0),
    leave_approved: acc.leave_approved + (s.leave_approved || 0),
    leave_pending: acc.leave_pending + (s.leave_pending || 0),
  }), { total: 0, absent: 0, leave_approved: 0, leave_pending: 0 });
  const subTotalPct = grandTotal > 0 ? (subTotal.total / grandTotal) * 100 : 0;

  return (
    <div className="md:col-span-2 bg-white rounded-xl border-2 border-red-200 shadow-sm overflow-hidden" data-testid="absent-repeat-offenders-card">
      <div className="px-4 py-2 border-b bg-red-50 text-sm font-semibold text-red-900 flex items-center justify-between gap-2 flex-wrap">
        <span>🚩 Repeat Offenders — {threshold}+ days absent / on leave in range</span>
        <div className="flex items-center gap-2 text-xs font-normal">
          <span className="text-red-700">
            Showing <b>{sorted.length}</b> of <b>{offenders.length}</b>
          </span>
          {hasAnyFilter && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-2 py-1 bg-white hover:bg-red-100 border border-red-300 text-red-700 rounded text-[11px] font-medium"
              data-testid="absent-repeat-clear-filters"
            >
              <X size={11} /> Clear filters
            </button>
          )}
          <span className="text-red-700">Action recommended</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-red-50/40 border-b">
            <tr>
              <SortHeader label="Staff" col="name" sortBy={sortBy} onSort={handleSort} align="left" />
              <SortHeader label="Geofence" col="geofence" sortBy={sortBy} onSort={handleSort} align="left" />
              <SortHeader label="Department" col="department" sortBy={sortBy} onSort={handleSort} align="left" />
              <SortHeader label="Days Off" col="total" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="% of Total" col="pct" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="Absent" col="absent" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="Leave (Apprvd)" col="leave_approved" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="Leave (Pend)" col="leave_pending" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="Last Date" col="last_date" sortBy={sortBy} onSort={handleSort} />
            </tr>
            <tr className="bg-white border-b">
              <FilterTextCell value={filters.name} onChange={(v) => setFilters({ ...filters, name: v })} placeholder="Search name / ID…" testid="aro-filter-name" />
              <FilterTextCell value={filters.geofence} onChange={(v) => setFilters({ ...filters, geofence: v })} placeholder="Search geofence…" testid="aro-filter-geofence" />
              <FilterTextCell value={filters.department} onChange={(v) => setFilters({ ...filters, department: v })} placeholder="Search dept…" testid="aro-filter-department" />
              <FilterNumRangeCell minVal={filters.total_min} maxVal={filters.total_max} onMin={(v) => setFilters({ ...filters, total_min: v })} onMax={(v) => setFilters({ ...filters, total_max: v })} testid="aro-filter-total" />
              <FilterNumRangeCell minVal={filters.pct_min} maxVal={filters.pct_max} onMin={(v) => setFilters({ ...filters, pct_min: v })} onMax={(v) => setFilters({ ...filters, pct_max: v })} testid="aro-filter-pct" step="0.1" />
              <FilterNumRangeCell minVal={filters.absent_min} maxVal={filters.absent_max} onMin={(v) => setFilters({ ...filters, absent_min: v })} onMax={(v) => setFilters({ ...filters, absent_max: v })} testid="aro-filter-absent" />
              <FilterNumRangeCell minVal={filters.leave_approved_min} maxVal={filters.leave_approved_max} onMin={(v) => setFilters({ ...filters, leave_approved_min: v })} onMax={(v) => setFilters({ ...filters, leave_approved_max: v })} testid="aro-filter-leaveapp" />
              <FilterNumRangeCell minVal={filters.leave_pending_min} maxVal={filters.leave_pending_max} onMin={(v) => setFilters({ ...filters, leave_pending_min: v })} onMax={(v) => setFilters({ ...filters, leave_pending_max: v })} testid="aro-filter-leavepend" />
              <FilterDateRangeCell minVal={filters.last_date_min} maxVal={filters.last_date_max} onMin={(v) => setFilters({ ...filters, last_date_min: v })} onMax={(v) => setFilters({ ...filters, last_date_max: v })} testid="aro-filter-lastdate" />
            </tr>
          </thead>
          <tbody className="divide-y divide-red-100">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500 text-xs italic">
                  No rows match the current filters.{' '}
                  <button onClick={clearFilters} className="text-red-700 underline hover:no-underline">Clear filters</button>{' '}
                  to see all {offenders.length} offenders.
                </td>
              </tr>
            ) : sorted.map(s => (
              <tr key={s.sid} data-testid={`absent-repeat-row-${s.sid}`}>
                <td className="px-3 py-1.5 text-gray-900 font-semibold">
                  {s.name} <span className="text-gray-400 font-mono">{s.sid}</span>
                </td>
                <td className="px-3 py-1.5 text-gray-700 truncate max-w-[160px]" title={s.geofence}>{s.geofence}</td>
                <td className="px-3 py-1.5 text-gray-700 truncate max-w-[160px]" title={s.department}>{s.department}</td>
                <td className="px-3 py-1.5 text-center font-bold text-red-700 tabular-nums">{s.total}</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-red-700">{s.pct.toFixed(1)}%</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-rose-700">{s.absent || 0}</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-emerald-700">{s.leave_approved || 0}</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-amber-700">{s.leave_pending || 0}</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-gray-600">{s.last_date || '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-red-50/60 border-t-2 border-red-200">
            <tr data-testid="absent-repeat-totals">
              <td colSpan={3} className="px-3 py-2 text-right text-[11px] font-bold text-red-900 uppercase">
                {hasAnyFilter ? 'Filtered subtotal' : 'Repeat offenders subtotal'}
              </td>
              <td className="px-3 py-2 text-center font-bold text-red-800 tabular-nums">{subTotal.total}</td>
              <td className="px-3 py-2 text-center font-bold text-red-800 tabular-nums">{subTotalPct.toFixed(1)}%</td>
              <td className="px-3 py-2 text-center font-bold text-rose-700 tabular-nums">{subTotal.absent}</td>
              <td className="px-3 py-2 text-center font-bold text-emerald-700 tabular-nums">{subTotal.leave_approved}</td>
              <td className="px-3 py-2 text-center font-bold text-amber-700 tabular-nums">{subTotal.leave_pending}</td>
              <td className="px-3 py-2 text-center text-red-700 text-[10px]">—</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function SortHeader({ label, col, sortBy, onSort, align = 'center' }) {
  const isActive = sortBy.key === col;
  const Icon = !isActive ? ArrowUpDown : (sortBy.dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-2 text-${align} text-[10px] font-medium text-red-900 uppercase cursor-pointer select-none hover:bg-red-100/60`}
      data-testid={`aro-sort-${col}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'left' ? '' : 'justify-center'}`}>
        {label}
        <Icon size={10} className={isActive ? 'text-red-700' : 'text-red-300'} />
      </span>
    </th>
  );
}

function FilterTextCell({ value, onChange, placeholder, testid }) {
  return (
    <td className="px-2 py-1.5">
      <div className="relative">
        <Search size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-5 pr-1.5 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-red-300"
          data-testid={testid}
        />
      </div>
    </td>
  );
}

function FilterNumRangeCell({ minVal, maxVal, onMin, onMax, testid, step = '1' }) {
  return (
    <td className="px-1 py-1.5">
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={minVal}
          onChange={(e) => onMin(e.target.value)}
          placeholder="min"
          step={step}
          className="w-full px-1 py-1 text-[11px] text-center border border-gray-200 rounded focus:outline-none focus:border-red-300 tabular-nums"
          data-testid={`${testid}-min`}
        />
        <input
          type="number"
          value={maxVal}
          onChange={(e) => onMax(e.target.value)}
          placeholder="max"
          step={step}
          className="w-full px-1 py-1 text-[11px] text-center border border-gray-200 rounded focus:outline-none focus:border-red-300 tabular-nums"
          data-testid={`${testid}-max`}
        />
      </div>
    </td>
  );
}

function FilterDateRangeCell({ minVal, maxVal, onMin, onMax, testid }) {
  return (
    <td className="px-1 py-1.5">
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={minVal}
          onChange={(e) => onMin(e.target.value)}
          className="w-full px-1 py-1 text-[10px] border border-gray-200 rounded focus:outline-none focus:border-red-300"
          data-testid={`${testid}-min`}
        />
        <input
          type="date"
          value={maxVal}
          onChange={(e) => onMax(e.target.value)}
          className="w-full px-1 py-1 text-[10px] border border-gray-200 rounded focus:outline-none focus:border-red-300"
          data-testid={`${testid}-max`}
        />
      </div>
    </td>
  );
}
